import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';

export type WorkerHealthy = 'green' | 'amber' | 'red';

export interface WorkerHeartbeat {
  lastTickAt: string | null;
  lastTickStatus: 'success' | 'error' | null;
  ageMs: number | null;
  staleThresholdMs: number;
  pollIntervalMs: number;
  healthy: WorkerHealthy;
}

interface UseWorkerHeartbeatResult {
  data: WorkerHeartbeat | null;
  isLoading: boolean;
  error: Error | null;
}

const DEFAULT_POLL_INTERVAL_MS = 30_000;

// Canned mockup-mode response. Matches the shape the backend returns for a
// fresh heartbeat at roughly the 45-second mark — enough to verify the card
// in mockup mode without needing a fixture file.
const MOCK_HEARTBEAT: WorkerHeartbeat = {
  lastTickAt: '2026-04-13T16:59:15Z',
  lastTickStatus: 'success',
  ageMs: 45_000,
  staleThresholdMs: 2_700_000,
  pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
  healthy: 'green',
};

/**
 * Live poll of `GET /api/v1/worker/heartbeat`. The refetch cadence is
 * driven by the server's `pollIntervalMs` field (tier-2
 * `WORKER_HEARTBEAT_POLL_INTERVAL_MS`, default 30s) so operators can
 * tune the poll rate without a frontend rebuild.
 *
 * The endpoint is member-role and tenant-agnostic — every authenticated
 * user may read it, and mocking mode returns a canned green response.
 */
export function useWorkerHeartbeat(): UseWorkerHeartbeatResult {
  const { mockupMode } = useAppContext();
  const getToken = useToken();

  const query = useQuery<WorkerHeartbeat>({
    queryKey: ['workerHeartbeat'],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<WorkerHeartbeat>('/api/v1/worker/heartbeat', token);
    },
    refetchInterval: (q) => q.state.data?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    enabled: !mockupMode,
  });

  if (mockupMode) return { data: MOCK_HEARTBEAT, isLoading: false, error: null };
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
  };
}
